import { useState } from 'react';
import { ConfirmButton } from '../../components/ConfirmButton';
import {
  useAdminAircraft,
  useAdminAircraftTypes,
  useCreateAircraft,
  useCreateAircraftType,
  useDeleteAircraft,
  useDeleteAircraftType,
  useUpdateAircraft,
  useUpdateAircraftType,
} from '../../hooks/use-admin';
import { ApiError } from '../../lib/api-client';

type Tab = 'aircraft' | 'types';

export function AdminAircraftPage() {
  const [tab, setTab] = useState<Tab>('aircraft');
  return (
    <div className="px-4 py-4 space-y-3">
      <h1 className="text-xl font-semibold">Flugzeuge & Typen</h1>
      <div className="flex gap-1 rounded-xl border border-slate-800 bg-slate-900/60 p-1">
        <TabBtn active={tab === 'aircraft'} onClick={() => setTab('aircraft')}>
          Flugzeuge
        </TabBtn>
        <TabBtn active={tab === 'types'} onClick={() => setTab('types')}>
          Flugzeugtypen
        </TabBtn>
      </div>
      {tab === 'aircraft' ? <AircraftSection /> : <TypesSection />}
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 rounded-lg px-3 py-2 text-sm transition-colors ${
        active
          ? 'bg-slate-800 text-slate-100'
          : 'text-slate-400 hover:text-slate-200'
      }`}
    >
      {children}
    </button>
  );
}

// ---------------- Aircraft ----------------

function AircraftSection() {
  const list = useAdminAircraft();
  const types = useAdminAircraftTypes();
  const create = useCreateAircraft();
  const update = useUpdateAircraft();
  const del = useDeleteAircraft();

  const [registration, setRegistration] = useState('');
  const [typeId, setTypeId] = useState('');

  const onCreate = () => {
    if (!registration.trim() || !typeId) return;
    create.mutate(
      { registration: registration.trim().toUpperCase(), typeId, active: true },
      {
        onSuccess: () => {
          setRegistration('');
          setTypeId('');
        },
      },
    );
  };

  const createErr =
    create.error instanceof ApiError ? create.error.message : null;
  const delErr = del.error instanceof ApiError ? del.error.message : null;

  return (
    <>
      <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-3 space-y-2">
        <label className="text-xs text-slate-400">Neues Flugzeug</label>
        <div className="flex gap-2">
          <input
            value={registration}
            onChange={(e) => setRegistration(e.target.value)}
            placeholder="z. B. D-1234"
            className="flex-1 rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm uppercase"
          />
          <select
            value={typeId}
            onChange={(e) => setTypeId(e.target.value)}
            className="rounded-lg border border-slate-700 bg-slate-950 px-2 py-2 text-sm"
          >
            <option value="">Typ wählen</option>
            {types.data?.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          <button
            onClick={onCreate}
            disabled={create.isPending || !registration.trim() || !typeId}
            className="rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium hover:bg-brand-700 disabled:opacity-60"
          >
            +
          </button>
        </div>
        {createErr && <p className="text-xs text-rose-400">{createErr}</p>}
      </section>

      {delErr && (
        <p className="rounded-lg border border-rose-900 bg-rose-950/40 p-2 text-xs text-rose-200">
          {delErr}
        </p>
      )}

      {list.isLoading ? (
        <p className="py-12 text-center text-sm text-slate-400">Lädt …</p>
      ) : (
        <ul className="space-y-2">
          {list.data?.map((a) => (
            <li
              key={a.id}
              className="rounded-xl border border-slate-800 bg-slate-900/60 p-3"
            >
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-sm font-medium font-mono">
                    {a.registration}
                  </p>
                  <p className="text-xs text-slate-400">{a.type?.name ?? '—'}</p>
                </div>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-1 text-xs text-slate-400">
                    <input
                      type="checkbox"
                      checked={a.active}
                      onChange={(e) =>
                        update.mutate({
                          id: a.id,
                          data: { active: e.target.checked },
                        })
                      }
                      className="h-4 w-4 accent-brand-600"
                    />
                    aktiv
                  </label>
                  <ConfirmButton
                    label="Löschen"
                    confirmLabel={`${a.registration} löschen?`}
                    onConfirm={() => del.mutate(a.id)}
                  />
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

// ---------------- Types ----------------

function TypesSection() {
  const list = useAdminAircraftTypes();
  const create = useCreateAircraftType();
  const update = useUpdateAircraftType();
  const del = useDeleteAircraftType();

  const [name, setName] = useState('');
  const createErr =
    create.error instanceof ApiError ? create.error.message : null;
  const delErr = del.error instanceof ApiError ? del.error.message : null;

  return (
    <>
      <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-3 space-y-2">
        <label className="text-xs text-slate-400">Neuer Typ</label>
        <div className="flex gap-2">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z. B. LS8"
            className="flex-1 rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
          />
          <button
            onClick={() => {
              if (!name.trim()) return;
              create.mutate(
                { name: name.trim(), active: true },
                { onSuccess: () => setName('') },
              );
            }}
            disabled={create.isPending || !name.trim()}
            className="rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium hover:bg-brand-700 disabled:opacity-60"
          >
            +
          </button>
        </div>
        {createErr && <p className="text-xs text-rose-400">{createErr}</p>}
      </section>

      {delErr && (
        <p className="rounded-lg border border-rose-900 bg-rose-950/40 p-2 text-xs text-rose-200">
          {delErr}
        </p>
      )}

      {list.isLoading ? (
        <p className="py-12 text-center text-sm text-slate-400">Lädt …</p>
      ) : (
        <ul className="space-y-2">
          {list.data?.map((t) => (
            <li
              key={t.id}
              className="rounded-xl border border-slate-800 bg-slate-900/60 p-3 flex items-center justify-between gap-2"
            >
              <span className="text-sm">{t.name}</span>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1 text-xs text-slate-400">
                  <input
                    type="checkbox"
                    checked={t.active}
                    onChange={(e) =>
                      update.mutate({
                        id: t.id,
                        data: { active: e.target.checked },
                      })
                    }
                    className="h-4 w-4 accent-brand-600"
                  />
                  aktiv
                </label>
                <ConfirmButton
                  label="Löschen"
                  confirmLabel={`"${t.name}" löschen?`}
                  onConfirm={() => del.mutate(t.id)}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
